import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getAuthHeader, handleUnauthorized } from '../api/core'
import {
  deriveCreditNumber,
  nextModeOnSystemDerive,
  nextModeOnUserEdit,
  resetCreditDocumentNumberOnEnable,
} from './creditDocumentNumberMode'
import type { CreditDocumentNumberMode } from './creditDocumentNumberMode'
import type { DebitCreditSetResult, FormState } from '../types/testdata.types'

function base64ToBlob(base64: string, mimeType: string): Blob {
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return new Blob([bytes], { type: mimeType })
}

/** Client-side best-effort preview van de creditboodschap — de PDF/e-mail blijft de bron van waarheid. */
export function previewCreditMessage(
  creditDocumentNumber: string,
  debitDocumentNumber: string,
  lines: Array<{ quantity: number; unit_price: number; vat_percentage: number }>,
  locale: string,
  creditedLineIndex: number | null = null,
): string {
  if (!debitDocumentNumber.trim() || !creditDocumentNumber.trim()) return ''
  const creditedLines = creditedLineIndex !== null ? lines.slice(creditedLineIndex, creditedLineIndex + 1) : lines
  const total = creditedLines.reduce((sum, l) => {
    const net = Number(l.quantity) * Number(l.unit_price)
    return sum + net + (net * Number(l.vat_percentage) / 100)
  }, 0)
  const amount = locale === 'en-GB'
    ? total.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : total.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return locale === 'en-GB'
    ? `This credits invoice number ${debitDocumentNumber} for an amount of ${amount}`
    : `Hierbij crediteren wij factuurnummer ${debitDocumentNumber} met bedrag ${amount}`
}

export type { CreditDocumentNumberMode }

interface UseDebitCreditSetOptions {
  buildRequestBody: () => Record<string, unknown>
  validateForm: () => boolean
  form: FormState
  refreshDocCounter: () => void
  ensureCurrentAutoDocumentNumber: (currentVisibleValue: string) => Promise<string>
}

export function useDebitCreditSet(opts: UseDebitCreditSetOptions) {
  const { t } = useTranslation()
  const [enabledState, setEnabledState] = useState(false)
  const [creditDocumentNumber, setCreditDocumentNumberState] = useState('')
  // Event-driven ownership (REQ-TESTDATA-002-correctie): expliciet gezet door
  // ÓF een echte user-edit ÓF een systeem-afleiding — NOOIT afgeleid uit een
  // vergelijking tussen de huidige waarde en de afgeleide waarde. Die
  // equality-check was de bug: als de gebruiker toevallig weer op exact de
  // afgeleide string uitkwam, werd het veld ten onrechte terug naar
  // auto_derived geclassificeerd, waarna een latere debet-wijziging het
  // "handmatige" creditnummer alsnog overschreef.
  const creditModeRef = useRef<CreditDocumentNumberMode>('auto_derived')

  /** Echte user-edit — ALTIJD manual, ongeacht de resulterende stringwaarde
   * (ook als die toevallig gelijk is aan wat afgeleid zou zijn). Dit is de
   * enige functie die vanuit een user-inputhandler aangeroepen mag worden. */
  const setCreditDocumentNumberFromUser = (value: string) => {
    creditModeRef.current = nextModeOnUserEdit()
    setCreditDocumentNumberState(value)
  }

  /** Systeem-afgeleide update — zet/behoudt auto_derived. Wordt nooit vanuit
   * een user-inputhandler aangeroepen; alleen door de afleidingslogica hier
   * in dit hook zelf. */
  const setDerivedCreditDocumentNumber = (value: string) => {
    creditModeRef.current = nextModeOnSystemDerive()
    setCreditDocumentNumberState(value)
  }

  useEffect(() => {
    if (creditModeRef.current !== 'auto_derived') return
    setDerivedCreditDocumentNumber(deriveCreditNumber(opts.form.document_number))
  }, [opts.form.document_number])

  /** Reset-trigger 1 (manual -> auto_derived): opnieuw inschakelen van de
   * debet/credit-set. Dit is de kleinste, al bestaande lifecycle-trigger
   * ("nieuwe sessie" — de gebruiker zet de set expliciet weer aan) en
   * introduceert geen nieuwe UI. Uitschakelen laat de ownership-state met
   * rust — er is dan toch geen zichtbaar creditveld meer.
   *
   * BELANGRIJK: reset zowel de mode ALS de zichtbare waarde in dezelfde
   * operatie (`resetCreditDocumentNumberOnEnable`), rechtstreeks afgeleid
   * van de HUIDIGE debetwaarde. Alleen de mode terugzetten was onvoldoende
   * — de useEffect hierboven reageert enkel op wijzigingen van het
   * debetnummer zelf, niet op een ownership-flip, dus zonder deze expliciete
   * herafleiding kon een oud handmatig creditnummer zichtbaar blijven staan
   * terwijl de request al auto_derived was — UI en submit-identity liepen
   * dan uiteen. */
  const setEnabled = (value: boolean) => {
    if (value) {
      const reset = resetCreditDocumentNumberOnEnable(opts.form.document_number)
      creditModeRef.current = reset.mode
      setCreditDocumentNumberState(reset.value)
    }
    setEnabledState(value)
  }

  // null = hele factuur crediteren (alle regels, standaardgedrag); anders index van de
  // ene debetregel die de creditnota moet crediteren (partiële creditering).
  const [creditedLineIndex, setCreditedLineIndex] = useState<number | null>(null)
  const [result, setResult] = useState<DebitCreditSetResult | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showEmailModal, setShowEmailModal] = useState(false)

  const buildSetRequestBody = () => ({
    ...opts.buildRequestBody(),
    document_type: 'invoice',
    credit_document_number: creditDocumentNumber,
    // REQ-TESTDATA-002: expliciete, event-driven ownership-state — nooit uit
    // een string-vergelijking afgeleid (zie creditModeRef hierboven).
    credit_document_number_mode: creditModeRef.current,
    credited_line_index: creditedLineIndex,
  })

  /** Pre-submit resync van de twee nummers die deze set gebruikt: de debet-
   * factuur (via de gedeelde useDocNumber-sync) en, alléén zolang het
   * creditnummer nog auto_derived is (`creditModeRef`), het daarvan
   * afgeleide creditnummer. Een handmatig creditnummer blijft ongewijzigd —
   * dat is de al bestaande, aparte ownership-mechaniek van dit hook (zie
   * boven), niet iets wat deze functie herdefinieert.
   *
   * BELANGRIJK (REQ-TESTDATA-002): dit is UX/advisory, niet meer de bron van
   * waarheid. Bij `credit_document_number_mode: "auto_derived"` leidt de
   * backend het daadwerkelijke creditnummer zelf af van het uiteindelijke,
   * authoritative debetnummer — nooit van deze client-gepeekte waarde. Onder
   * concurrency kan de hier berekende `freshCreditNumber` dus afwijken van
   * wat de server werkelijk toekent; de response van de aanroepende
   * submit-functie draagt de echte waarden (zie generateSet()/de
   * send-email-endpoints, die "documents" teruggeven).
   *
   * Retourneert de verse waarden rechtstreeks (niet via form-state herlezen
   * ná deze await) zodat de aanroeper ze direct in de request-payload kan
   * gebruiken. */
  const resolveDynamicFields = async (): Promise<{ document_number: string; credit_document_number: string }> => {
    const freshDebitNumber = await opts.ensureCurrentAutoDocumentNumber(opts.form.document_number)
    const isAutoDerived = creditModeRef.current === 'auto_derived'
    const freshCreditNumber = isAutoDerived ? deriveCreditNumber(freshDebitNumber) : creditDocumentNumber
    if (isAutoDerived && freshCreditNumber !== creditDocumentNumber) {
      setDerivedCreditDocumentNumber(freshCreditNumber)
    }
    return { document_number: freshDebitNumber, credit_document_number: freshCreditNumber }
  }

  const validateSet = (): boolean => {
    if (!opts.validateForm()) return false
    if (!creditDocumentNumber.trim()) {
      setError(t('debit_credit_set.error_credit_number_required'))
      return false
    }
    return true
  }

  const generateSet = async () => {
    setError(null)
    setResult(null)
    if (!validateSet()) return

    setLoading(true)
    try {
      const dynamic = await resolveDynamicFields()
      const body = { ...buildSetRequestBody(), ...dynamic }
      const res = await fetch('/api/testdata/generate-debit-credit-set', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: getAuthHeader() },
        body: JSON.stringify(body),
      })
      handleUnauthorized(res)
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: res.statusText }))
        throw new Error(err.detail ?? res.statusText)
      }
      const data: DebitCreditSetResult = await res.json()
      setResult(data)

      const blob = base64ToBlob(data.zip_base64, 'application/zip')
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = data.zip_filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)

      opts.refreshDocCounter()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('debit_credit_set.error_generate_failed'))
    } finally {
      setLoading(false)
    }
  }

  const openEmailModal = () => {
    setError(null)
    if (!validateSet()) return
    setShowEmailModal(true)
  }

  return {
    enabled: enabledState, setEnabled,
    creditDocumentNumber, setCreditDocumentNumber: setCreditDocumentNumberFromUser,
    creditedLineIndex, setCreditedLineIndex,
    result, clearResult: () => setResult(null),
    loading,
    error, setError,
    generateSet,
    buildSetRequestBody,
    resolveDynamicFields,
    showEmailModal, setShowEmailModal,
    openEmailModal,
  }
}

export type UseDebitCreditSetReturn = ReturnType<typeof useDebitCreditSet>
